
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TimePicker } from "@/components/ui/time-picker";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, MoveUp, MoveDown, Clock, Sun, Moon, RotateCcw, FlaskConical } from "lucide-react";
import { useSleepSchedule } from './SleepScheduleContext';
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ManageScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caregiverId?: string;
  childId?: string;
  selectedDate?: Date;
}

export function ManageScheduleDialog({ 
  open, 
  onOpenChange,
  caregiverId: caregiverIdProp,
  childId,
  selectedDate
}: ManageScheduleDialogProps) {
  const { 
    scheduleItems, 
    createScheduleItem, 
    updateSchedule, 
    resetToRecommendations, 
    generateAIRecommendation,
    isLoadingRecommendation,
    hasCustomSchedule,
    childInfo,
    caregiverId: contextCaregiverId
  } = useSleepSchedule();
  
  // Use prop caregiverId if provided, otherwise use context caregiverId
  const caregiverId = caregiverIdProp || contextCaregiverId;
  const { toast } = useToast();
  const [localScheduleItems, setLocalScheduleItems] = useState(() => 
    scheduleItems.map(item => ({...item}))
  );
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [repeatDays, setRepeatDays] = useState(1);
  
  // Reset local items when dialog opens or scheduleItems change
  React.useEffect(() => {
    if (open) {
      setLocalScheduleItems([...scheduleItems]);
    }
  }, [open, scheduleItems]);
  
  const handleAddItem = (type: 'wake' | 'nap' | 'bedtime') => {
    const newItem = {
      id: `temp-${Date.now()}`,
      type,
      label: type === 'wake' ? `Wake window ${localScheduleItems.filter(i => i.type === 'wake').length + 1}` : 
             type === 'nap' ? `Nap ${localScheduleItems.filter(i => i.type === 'nap').length + 1}` : 
             'Bedtime',
      scheduledStartTime: `2024-01-01T${type === 'wake' ? '07:00:00' : type === 'bedtime' ? '19:00:00' : '12:00:00'}`,
      status: 'on-time' as const,
    };
    
    // Adding appropriate fields based on the type
    if (type === 'wake') {
      newItem['scheduledEndTime'] = `2024-01-01T12:00:00`;
      newItem['targetDuration'] = "3h";
      newItem['duration'] = "3h";
    } else if (type === 'nap') {
      newItem['duration'] = "1h 30min";
      newItem['targetDuration'] = "1h 30min";
    } else {
      // Bedtime
      newItem['duration'] = "19:00";
      newItem['targetDuration'] = "19:00";
    }
    
    setLocalScheduleItems([...localScheduleItems, newItem]);
  };
  
  const handleRemoveItem = (index: number) => {
    const newItems = [...localScheduleItems];
    newItems.splice(index, 1);
    setLocalScheduleItems(newItems);
  };
  
  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || 
        (direction === 'down' && index === localScheduleItems.length - 1)) {
      return;
    }
    
    const newItems = [...localScheduleItems];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    setLocalScheduleItems(newItems);
  };
  
  const handleItemChange = (index: number, field: string, value: string) => {
    const newItems = [...localScheduleItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    
    setLocalScheduleItems(newItems);
  };
  
  const handleTimeChange = (index: number, field: 'scheduledStartTime' | 'scheduledEndTime', value: string) => {
    const newItems = [...localScheduleItems];
    
    // Update just the time portion of the full datetime string
    const currentDatePart = newItems[index][field]?.split('T')[0] || '2024-01-01';
    newItems[index][field] = `${currentDatePart}T${value}:00`;
    
    setLocalScheduleItems(newItems);
  };
  
  const handleSave = async () => {
    console.log('🔍 [ManageScheduleDialog] handleSave called with:', {
      propCaregiverId: caregiverIdProp,
      contextCaregiverId,
      effectiveCaregiverId: caregiverId,
      childId,
      localScheduleItemsCount: localScheduleItems.length,
      selectedDate: selectedDate?.toISOString(),
      repeatDays
    });
    
    setSaving(true);
    try {
      await updateSchedule(localScheduleItems, childId, caregiverId || undefined, repeatDays);
      toast({
        title: "Schedule saved",
        description: `Custom schedule has been saved successfully for ${repeatDays} day${repeatDays > 1 ? 's' : ''}.`,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('❌ [ManageScheduleDialog] Error saving schedule:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        propCaregiverId: caregiverIdProp,
        contextCaregiverId,
        effectiveCaregiverId: caregiverId,
        childId,
        localScheduleItemsCount: localScheduleItems.length,
        repeatDays
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to save schedule';
      toast({
        title: "Error",
        description: `${errorMessage}. Check console for details.`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!childId) {
      toast({
        title: "Error",
        description: "Please select a child to generate recommendations.",
        variant: "destructive",
      });
      return;
    }

    try {
      await generateAIRecommendation(childId, selectedDate);
      toast({
        title: "AI Recommendations Generated",
        description: "Schedule created using pediatric sleep science and child's age.",
        duration: 2000,
      });
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      toast({
        title: "Error",
        description: "Failed to generate AI recommendations. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReset = async () => {
    if (!childId) {
      toast({
        title: "Error",
        description: "Child ID is required to reset schedule.",
        variant: "destructive",
      });
      return;
    }

    setResetting(true);
    try {
      await resetToRecommendations(childId, selectedDate);
      toast({
        title: "Schedule reset",
        description: "Reverted to SleepNest AI recommendations.",
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error resetting schedule:', error);
      toast({
        title: "Error",
        description: "Failed to reset schedule. Please try again.",
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  };
  
  const formatTime = (timeString: string | undefined) => {
    if (!timeString) return '';
    return timeString.split('T')[1].substring(0, 5);
  };
  
  const getItemIcon = (type: string) => {
    switch(type) {
      case 'wake':
        return <Sun className="h-4 w-4 text-amber-500" />;
      case 'nap':
        return <Moon className="h-4 w-4 text-blue-500 rotate-180" />;
      case 'bedtime':
        return <Moon className="h-4 w-4 text-indigo-500" />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl lg:max-w-5xl xl:max-w-7xl max-h-[95vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="pb-6 border-b px-6 pt-6">
          <DialogTitle className="text-2xl sm:text-3xl font-semibold mb-3">
            Manage Sleep Schedule
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Create and customize sleep schedules based on pediatric sleep science
          </DialogDescription>
          
          {childId && (
            <div className="mt-4 px-4 py-3 bg-primary/5 rounded-lg border border-primary/10">
              <p className="text-sm font-medium">
                Creating schedule for: <span className="font-semibold text-primary">{childInfo?.name || 'Child'}</span>
              </p>
              {childInfo?.dateOfBirth && (
                <p className="text-xs text-muted-foreground mt-1">
                  Age: {(() => {
                    const dob = new Date(childInfo.dateOfBirth);
                    const now = new Date();
                    const months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
                    const years = Math.floor(months / 12);
                    const remainingMonths = months % 12;
                    return years > 0 ? `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}` : `${months} month${months !== 1 ? 's' : ''}`;
                  })()}
                </p>
              )}
            </div>
          )}
          {!childId && (
            <Alert className="mt-4 border-destructive/50 bg-destructive/5">
              <AlertDescription className="text-sm">
                No child selected. Please select a child to create a schedule.
              </AlertDescription>
            </Alert>
          )}
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-8 py-6">
            {hasCustomSchedule && (
              <Alert className="border-primary/30 bg-primary/5">
                <AlertDescription className="text-sm">
                  This schedule has been customized. Use "Reset to AI Recommendations" below to revert to defaults.
                </AlertDescription>
              </Alert>
            )}

            {/* Repeat Days Section */}
            <div className="space-y-3 bg-accent/50 p-5 rounded-lg border border-border/50">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="repeat-days" className="text-sm font-semibold">
                  Schedule Duration
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  id="repeat-days"
                  type="number"
                  min="1"
                  max="30"
                  value={repeatDays}
                  onChange={(e) => setRepeatDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                  className="w-24 h-11"
                />
                <span className="text-sm font-medium">day{repeatDays > 1 ? 's' : ''}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This schedule will apply to <span className="font-medium text-foreground">{repeatDays} consecutive day{repeatDays > 1 ? 's' : ''}</span> starting from <span className="font-medium text-foreground">{selectedDate?.toLocaleDateString() || 'today'}</span>.
              </p>
            </div>

            {/* AI Recommendation Section */}
            <div className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-accent/30 to-primary/5 border border-primary/20 rounded-xl p-4 sm:p-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-primary flex items-center justify-center shadow-lg">
                    <FlaskConical className="h-6 w-6 sm:h-7 sm:w-7 text-primary-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">AI-Powered Schedule</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      Generate a personalized schedule based on pediatric sleep science, child's age, and recent sleep patterns.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleGenerateAI}
                  disabled={isLoadingRecommendation}
                  className="w-full h-11 px-6 shadow-sm"
                >
                  {isLoadingRecommendation ? 'Generating...' : 'Generate Schedule'}
                </Button>
              </div>
            </div>
            
            {/* Add Item Buttons */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-semibold">Add Schedule Items</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Button 
                  onClick={() => handleAddItem('wake')} 
                  variant="outline"
                  className="h-auto py-4 justify-start gap-3 hover:bg-accent hover:border-primary/30 transition-all"
                >
                  <Sun className="h-5 w-5 text-amber-500" />
                  <span className="font-medium">Wake Window</span>
                </Button>
                <Button 
                  onClick={() => handleAddItem('nap')} 
                  variant="outline"
                  className="h-auto py-4 justify-start gap-3 hover:bg-accent hover:border-primary/30 transition-all"
                >
                  <Moon className="h-5 w-5 text-blue-500 rotate-180" />
                  <span className="font-medium">Nap</span>
                </Button>
                <Button 
                  onClick={() => handleAddItem('bedtime')} 
                  variant="outline"
                  className="h-auto py-4 justify-start gap-3 hover:bg-accent hover:border-primary/30 transition-all"
                >
                  <Moon className="h-5 w-5 text-indigo-500" />
                  <span className="font-medium">Bedtime</span>
                </Button>
              </div>
            </div>
            
            {/* Schedule Items as Cards */}
            <div className="space-y-4">
              {localScheduleItems.length === 0 ? (
                <div className="text-center py-20 border-2 border-dashed border-border/50 rounded-xl bg-muted/20">
                  <div className="mb-5 inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                    <Clock className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-base font-medium mb-2">No schedule items yet</p>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Get started by using the buttons above to add wake windows, naps, or bedtime, or generate an AI-powered schedule.
                  </p>
                </div>
              ) : (
                localScheduleItems.map((item, index) => (
                  <div key={item.id} className="border border-border/50 rounded-xl p-5 bg-card hover:shadow-md transition-all hover:border-primary/30">
                    <div className="flex flex-col sm:flex-row items-start gap-5">
                      {/* Icon */}
                      <div className="flex-shrink-0 hidden sm:block">
                        <div className="w-11 h-11 rounded-lg bg-accent flex items-center justify-center">
                          {getItemIcon(item.type)}
                        </div>
                      </div>
                      
                      <div className="flex-1 w-full space-y-5">
                        {/* Label - Full Width */}
                        <div>
                          <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Item Label</Label>
                          <Input
                            value={item.label}
                            onChange={(e) => handleItemChange(index, 'label', e.target.value)}
                            className="h-11"
                            placeholder="Enter label for this schedule item"
                          />
                        </div>

                        {/* Time and Duration Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Start Time</Label>
                            <TimePicker
                              value={formatTime(item.scheduledStartTime)}
                              onChange={(value) => handleTimeChange(index, 'scheduledStartTime', value)}
                              className="h-11"
                            />
                          </div>

                          <div>
                            <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Duration</Label>
                            <Input
                              value={item.duration || ''}
                              onChange={(e) => handleItemChange(index, 'duration', e.target.value)}
                              placeholder="e.g. 1h 30m"
                              className="h-11"
                            />
                          </div>

                          {/* Target Range - Spans remaining space */}
                          <div className="sm:col-span-2 lg:col-span-1">
                            <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Target Range</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                value={item.targetDuration ? item.targetDuration.split(" to ")[0] : ''}
                                onChange={(e) => {
                                  const currentTarget = item.targetDuration?.split(" to ");
                                  const newTarget = currentTarget?.length > 1 
                                    ? `${e.target.value} to ${currentTarget[1]}`
                                    : e.target.value;
                                  handleItemChange(index, 'targetDuration', newTarget);
                                }}
                                placeholder="Min"
                                className="h-11"
                              />
                              <span className="text-xs text-muted-foreground font-medium">to</span>
                              <Input
                                value={item.targetDuration?.includes(" to ") 
                                  ? item.targetDuration.split(" to ")[1]
                                  : ''
                                }
                                onChange={(e) => {
                                  const currentTarget = item.targetDuration || '';
                                  const baseTarget = currentTarget.includes(" to ")
                                    ? currentTarget.split(" to ")[0]
                                    : currentTarget;
                                  
                                  const newTarget = e.target.value 
                                    ? `${baseTarget} to ${e.target.value}`
                                    : baseTarget;
                                    
                                  handleItemChange(index, 'targetDuration', newTarget);
                                }}
                                 placeholder="Max"
                                className="h-11"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex sm:flex-col flex-row justify-end gap-1.5 w-full sm:w-auto">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleMoveItem(index, 'up')}
                          disabled={index === 0}
                          className="h-9 w-9 p-0 hover:bg-accent disabled:opacity-30"
                          title="Move up"
                        >
                          <MoveUp className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleMoveItem(index, 'down')}
                          disabled={index === localScheduleItems.length - 1}
                          className="h-9 w-9 p-0 hover:bg-accent disabled:opacity-30"
                          title="Move down"
                        >
                          <MoveDown className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleRemoveItem(index)}
                          className="h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Notes Section */}
            <div className="space-y-3 bg-accent/30 p-5 rounded-lg border border-border/50">
              <Label htmlFor="schedule-notes" className="text-sm font-semibold flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                Schedule Notes
              </Label>
              <Textarea
                id="schedule-notes"
                placeholder="Add notes about this sleep schedule (e.g., The National Sleep Foundation suggests that little ones this age should get around 13.5hrs of total sleep per day. Aim for 11-12 hours at night.)"
                className="min-h-[100px] resize-none text-sm bg-background"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-6 border-t px-6 pb-6">
          <Button 
            variant="outline" 
            onClick={handleReset}
            disabled={resetting || !hasCustomSchedule}
            className="h-11 px-5 w-full sm:w-auto"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {resetting ? 'Resetting...' : 'Reset to AI Recommendations'}
          </Button>
          <div className="flex gap-3 sm:ml-auto w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              className="flex-1 sm:flex-none h-11 px-6"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="flex-1 sm:flex-none h-11 px-6 shadow-sm"
            >
              {saving ? 'Saving...' : 'Save Schedule'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
